import { FeatureFlag } from '@/types/featureFlag';

import { isFeatureFlag } from './flagCatalog';

/* A forced value per flag. A flag that is absent takes the value the server resolved; a flag
 * that is present is forced to the value it maps to, whatever the server said.
 */
export type FlagOverrides = Partial<Record<FeatureFlag, boolean>>;

// Drops keys that are not flags, so an override left behind by a renamed or deleted flag
// retires itself instead of sitting in storage forever.
export const sanitizeFlagOverrides = (stored: Record<string, boolean>): FlagOverrides =>
    Object.keys(stored)
        .filter(isFeatureFlag)
        .reduce<FlagOverrides>((overrides, flag) => {
            overrides[flag] = stored[flag];
            return overrides;
        }, {});

const withoutFlag = (overrides: FlagOverrides, flag: FeatureFlag): FlagOverrides =>
    (Object.keys(overrides) as FeatureFlag[])
        .filter((key) => key !== flag)
        .reduce<FlagOverrides>((remaining, key) => {
            remaining[key] = overrides[key];
            return remaining;
        }, {});

// A null value clears the flag back to whatever the server says.
export const withFlagOverride = (
    overrides: FlagOverrides,
    flag: FeatureFlag,
    value: boolean | null,
): FlagOverrides =>
    value === null ? withoutFlag(overrides, flag) : { ...overrides, [flag]: value };

/* Layers the forced values over the values the server resolved.
 *
 * The server answer is keyed by the caller's own names rather than by flag name — a caller
 * asks for `{ mapEnabled: 'ALL_VISITS_MAP' }` and reads `enabled.mapEnabled` — so the
 * mapping is needed to find the flag an entry stands for.
 *
 * Returns the input untouched when nothing is forced, which keeps the identity of the record
 * stable for callers that pass it into a dependency list.
 */
export const applyFlagOverrides = (
    features: Record<string, string>,
    enabledByKey: Record<string, boolean>,
    overrides: FlagOverrides,
): Record<string, boolean> => {
    if (Object.keys(overrides).length === 0) {
        return enabledByKey;
    }
    return Object.keys(enabledByKey).reduce<Record<string, boolean>>((enabled, key) => {
        const flag = features[key];
        const forced = isFeatureFlag(flag) ? overrides[flag] : undefined;
        enabled[key] = forced ?? enabledByKey[key];
        return enabled;
    }, {});
};
